const {Component} = require("react");
const echo = require("services/echo");
const channelStore = require("stores/ChannelsStore");
const messageStore = require("stores/MessagesStore");
const PropTypes = require("prop-types");
const {debounce} = require("helpers/throttle");
const {observer} = require("mobx-react");
const {decodeObservable} = require("helpers/object");
const {computed} = require("mobx");
const {Channel} = require("components/backend/chat/Channel");
const moment = require("moment");
const {isRead, isSent} = require("components/backend/chat/Message");
const {ChatWindow} = require("components/backend/chat/ChatWindow");
const {ChatInput} = require("components/backend/chat/ChatInput");
const userStore = require("stores/UserStore");
const Confirm = require("components/modals/Confirm");
const Loader = require("components/mixins/Loader");
const router = require("services/router");
const Censor = require("services/Censor");

/*
 -------------------------------
 Chat component
 -------------------------------
 */

@observer
class Chat extends Component {

	static get propTypes() {
		return {
			typingDebounceDelay: PropTypes.number,
			defaultAvatarUrl: PropTypes.string,
			viewAs: PropTypes.string,
		};
	}

	static get defaultProps() {
		return {
			typingDebounceDelay: 4000,
			defaultAvatarUrl: "img/avatar-placeholder.png",
			viewAs: "student",
		};
	}

	constructor(props) {
		super(props);

		const channels = this.channels;

		this.state = {
			channel: channels.length > 0 ? channels[0].id : null,
			typing: [],
			loading: false,
		};

		// Setup typing
		this.typingInstances = {};
	}

	componentDidMount() {
		this.subscribeToUserState();
		this.subscribeToChannels();
	}

	subscribeToChannels() {
		this.channels.forEach(channel => this.subscribeToChannel(channel));
	}

	subscribeToChannel(channel) {
		echo.chat(channel.id)
			.listen("App\\Events\\Chat\\MessageCreated", e => this.onMessageCreated(e.message))
			.listen("App\\Events\\Chat\\MessageChanged", e => this.onMessageChanged(e.message))
			.listenForWhisper("typing", () => this.onTypingWhisper(channel));
	}

	subscribeToUserState() {
		echo.userState(userStore.get("id"))
			.listen("App\\Events\\Chat\\ChannelCreated", e => this.onChannelCreated(e.channel));
	}

	/////////////////////////////////
	// Typing
	/////////////////////////////////

	typingInstance(channelId) {
		if (this.typingInstances[channelId] == undefined) {
			this.typingInstances[channelId] = debounce(this.props.typingDebounceDelay, () => this.stopTyping(channelId));
		}

		return this.typingInstances[channelId];
	}

	startTyping(channelId) {
		this.setState(state => {
			const index = state.typing.indexOf(channelId);

			if (index === -1) {
				state.typing.push(channelId);
			}

			return state;
		}, () => {
			// This trigger the debounces stopTyping for the channelId
			this.typingInstance(channelId)();
		});
	}

	stopTyping(channelId) {
		// This cancels the enqueued stopTyping fo the channelId
		this.typingInstance(channelId).cancel();

		this.setState(state => {
			const index = state.typing.indexOf(channelId);

			if (index > -1) {
				state.typing.splice(index, 1);
			}

			return state;
		});
	}

	isTyping(channelId) {
		return this.state.typing.indexOf(channelId) !== -1;
	}

	/////////////////////////////////
	// Accessors
	/////////////////////////////////

	@computed
	get channels() {
		return decodeObservable(channelStore.collection);
	}

	@computed
	get messages() {
		return decodeObservable(messageStore.collection);
	}

	@computed
	get activeMessages() {
		return decodeObservable(messageStore.collection)
			.filter(m => m.channel_id === this.activeChannel.id);
	}

	@computed
	get oldestActiveMessage()
	{
		return this.activeMessages
			.reduce((oldest, current) => {
				return current.created_at < oldest.created_at ? 
					current : oldest;
			}, this.activeMessages[0])

	}

	@computed
	get unreadMessages() {
		return this.messages.filter(message => !isSent(message) && !isRead(message));
	}

	@computed
	get activeChannel() {
		return this.state.channel ? this.getChannelById(this.state.channel) : null;
	}

	/////////////////////////////////
	// Helpers
	/////////////////////////////////

	getChannelById(channelId) {
		return this.channels.filter(channel => channel.id === channelId) [0];
	}

	getChannelMessages(channel) {
		return this.messages
			.filter(message => message.channel_id === channel.id)
			.sort((messageA, messageB) => {
				const dateA = moment(messageA.created_at);
				const dateB = moment(messageB.created_at);
				return dateA.isBefore(dateB) ? -1 : 1;
			});
	}

	/////////////////////////////////
	// Socket listeners
	/////////////////////////////////

	onChannelCreated = (channel) => {
		// Push new channel into store
		channelStore.add(channel);

		// Set as active channel if there is no other
		if (this.activeChannel == undefined) {
			this.setState({channel: channel.id});
		}

		// Subscribe to channel
		this.subscribeToChannel(channel);
	};

	onMessageChanged = (message) => {
		// Replace message in store
		messageStore.replace(message);
	};

	onMessageCreated = (message) => {
		// Push new message into store
		messageStore.add(message);

		// Run "stopTyping" timeout instantanously
		this.typingInstance(message.channel_id).run();

		// Update last_activity_at on channel
		let channel = this.getChannelById(message.channel_id);

		if (channel) {
			channel.last_activity_at = message.created_at;
		}
	};

	onTypingWhisper = (channel) => {
		this.startTyping(channel.id);
	};

	/////////////////////////////////
	// DOM Listeners
	/////////////////////////////////

	channelChangeListener(channel) {
		return (e) => {
			e.preventDefault();
			e.stopPropagation();

			this.setState({channel: channel.id}, () => {
				if (this.activeMessages.length < 2) {
					this.lazyLoad();
				}
			});
		};
	}

	inputTypingListener(channel) {
		return () => {
			echo.chat(channel.id).whisper("typing", {
				user_id: userStore.get("id"),
				channel_id: channel.id
			});
		};
	}

	inputSubmitListener(channel) {
		return (input) => {
			const last_name = userStore.get('name', '').split(' ').pop();
			const flagged = Censor.isFlagged(input, last_name);

			if (!flagged) {
				return this.createMessage(channel.id, input);
			}

			return this.msg_confirm
				.await(this.renderCensorWarning(), 'Advarsel')
				.then(() => this.createMessage(channel.id, input, flagged));
		};
	}

	createMessage = (channel_id, input, flagged = false) => {
		return messageStore.create({
			channel_id: channel_id,
			type: "message",
			contents: input,
			params: {flagged: flagged}
		});
	}

	handleReadMessages = (messages) => {
		if (userStore.get('is_impersonating', false)) return;

		messageStore.read(messages);
	};

	lazyLoad = () => {
		this.setState({loading: true});
		let oldestMsg = this.oldestActiveMessage;

		if (!oldestMsg) {
			return messageStore
				.fetch()
				.then(() => this.setState({loading: false}));
		}

		messageStore.append({
			channel_id: this.activeChannel.id,
			limit: 20,
			order: 'desc',
			period: {
				before: oldestMsg.created_at
			}
		}).then(() => {
			this.setState({loading: false});
		});
	}

	handleMessageAction = (action, messageId) => {
		return this.confirm
			.await(
				action === "accept" ?
					"Er du sikker på at du vil acceptere denne ændring?" :
					"Er du sikker på at du vil afvise denne ændring?"
			)
			.then(() => messageStore.act(action, messageId));
	};

	/////////////////////////////////
	// Rendering
	/////////////////////////////////

	renderCensorWarning() {
		const name = userStore.get('nickname');

		const for_teacher = (
			<React.Fragment>
				<p>Det ser ud til, at du er ved at sende dine private kontaktoplysninger for at forsætte kommunikationen uden om CleverMate.</p>
				<p>Vi gør opmærksom på, at det ikke er tilladt at lave aftaler uden om platformen, og at dette kan medføre udelukkelse. Vi håber meget, at du har forståelse for dette, og vil benytte dig af de fordele og den sikkerhed, som vi tilbyder, ved at bookinger og betaling foregår herigennem. Du kan læse mere om, hvad du får ud af at holde dig til platformen på <a target="_blank" href={router.route('advantages')}> følgende side.</a></p>
				<p>Såfremt du fortsat ønsker at sende beskeden i sin nuværende form, vil den blive gennemgået af en supportmedarbejder for at verificere at indholdet lever op til vores vilkår.</p>
			</React.Fragment>);

		const for_student = (
			<React.Fragment>
				<p>Det ligner, at du er ved at sende dine private kontaktoplysninger for at forsætte kommunikationen udenom CleverMate.</p>
				<p>Vi gør opmærksom på, at det ikke er tilladt at lave aftaler udenom platformen, og at dette kan medføre udelukkelse. Vi håber meget, at du har forståelse for dette, og vil benytte dig af de fordele og den sikkerhed, som vi tilbyder, ved at bookinger og betaling foregår herigennem. For fortsat at kunne tilbyde Danmarks billigste lektiehjælp og være kontakten imellem underviser og elev, er det vigtigt, at du ikke går uden om platformen.</p>
				<p>Såfremt du forsat ønsker at sende beskeden i sin nuværende form, vil den blive gennemgået af en supportmedarbejder for at verificere at indholdet lever op til vores vilkår.</p>
			</React.Fragment>);

		const content = this.props.viewAs === 'teacher' ?
			for_teacher :
			for_student;

		return (
			<React.Fragment>
				<p>Kære {name}</p>
				{content}
			</React.Fragment>
		);
	}

	renderChannels() {
		return this.channels.map(channel => {
			const active = this.state.channel === channel.id;
			const messages = this.getChannelMessages(channel);

			return (
				<Channel key={channel.id} {...channel}
								 messages={messages}
								 active={active}
								 onClick={this.channelChangeListener(channel)}/>
			);
		});
	}

	renderChannelContainer() {
		return (
			<div className="chat-messages">
				<div className="top-bar">
					<h1>Beskeder</h1>
					<div className="notifications">{this.unreadMessages.length}</div>
				</div>

				<div className="messages">{this.renderChannels()}</div>
			</div>
		);
	}

	renderMessageWindows() {
		return this.channels.map(channel => {
			const messages = this.getChannelMessages(channel);
			const active = this.state.channel === channel.id;

			return (
				<ChatWindow key={channel.id} channel={channel} messages={messages} active={active}
										handleMessageAction={this.handleMessageAction}
										handleRead={this.handleReadMessages}
										loading={this.state.loading}
										handleScrollToTop={this.lazyLoad} />
			);
		});
	}

	renderInput() {
		const channel = this.activeChannel;
		const typing = this.isTyping(channel.id);

		return (
			<ChatInput typing={typing}
								 channel={this.activeChannel}
								 handleTyping={this.inputTypingListener(channel)}
								 handleSubmit={this.inputSubmitListener(channel)}/>
		);
	}

	renderMessageContainer() {
		let channel = this.activeChannel;
		let partner = channel.members.filter(member => member.id !== userStore.get('id')) [0];

		return (
			<div className="message-content">
				<Loader active={this.state.loading}></Loader>
				<h3 className="nickname">{partner.nickname || null}</h3>
				{this.renderMessageWindows()}
				{this.renderInput()}
			</div>
		);
	}

	renderBody() {
		if (this.activeChannel) {
			return (
				<div className="chat-body">
					{this.renderChannelContainer()}
					{this.renderMessageContainer()}
				</div>
			);
		}

		// If there are no channels, return a message
		return (
			<div className="chat-body">
				<div className="no-messages">
					<h1>Beskeder</h1>
					<p>Du har ingen aktive samtaler på dette tidspunkt.</p>
				</div>
			</div>
		);
	}

	render() {
		return (
			<div className="teacher-chat controlpanel">
				{this.renderBody()}
				<Confirm ref={el => this.confirm = el}/>
				<Confirm ref={el => this.msg_confirm = el} no_styles={true} className="message-warning" confirm="Beskeden overholder vilkårene – send den" cancel="Forstået - jeg ønsker at redigere beskeden" />
			</div>
		);
	}

}

module.exports = Chat;



// WEBPACK FOOTER //
// ./resources/js/components/backend/chat/Chat.js